import { defineStore } from 'pinia';
import {
  getActiveAnnouncement,
  getAnnouncementUnreadCount,
  getAnnouncements,
  markAnnouncementRead,
  isAnnouncementUnread,
  type AnnouncementItem,
  type AnnouncementReadSource,
} from '@/api/announcements';
import { announcementApiLang } from '@/common/utils/announcementLocale';
import { useWalletStore } from '@/store/wallet';
import logger from '@/common/utils/logger';
import i18n from '@/locales';

const resolveRequestLang = () => {
  try {
    return announcementApiLang(String(i18n.global.locale.value || 'en'));
  } catch {
    return 'en' as const;
  }
};

const announcementStatus = (item: AnnouncementItem | null | undefined) =>
  String(item?.status || '').trim().toUpperCase();

/** 用户历史可见：与 Business USER_VISIBLE_STATUSES 一致 */
const isUserHistoryAnnouncement = (item: AnnouncementItem | null | undefined) => {
  const status = announcementStatus(item);
  return status === 'PUBLISHED' || status === 'ARCHIVED';
};

const isPublishedAnnouncement = (item: AnnouncementItem | null | undefined) =>
  announcementStatus(item) === 'PUBLISHED';

/** 合并并发 active 请求；鉴权晚到时排队再拉一次，避免刷新吞掉带 JWT 的结果 */
let activeFetchPromise: Promise<void> | null = null;
let activeFetchQueued = false;

export const useAnnouncementsStore = defineStore('announcements', {
  state: () => ({
    active: null as AnnouncementItem | null,
    bannerVisible: false,
    unreadCount: 0,
    list: [] as AnnouncementItem[],
    listPage: 1,
    listPageSize: 10,
    listTotal: 0,
    listLoading: false,
    listHasMore: true,
    activeLoading: false,
    unreadLoading: false,
  }),

  getters: {
    /** 菜单角标：0 不展示 */
    badgeCount: (s) => (s.unreadCount > 0 ? s.unreadCount : 0),
  },

  actions: {
    /** 冷启动 / 回前台 / 任意页：拉当前公告（建议带 JWT） */
    async fetchActive() {
      if (activeFetchPromise) {
        activeFetchQueued = true;
        return activeFetchPromise;
      }
      this.activeLoading = true;
      activeFetchPromise = (async () => {
        try {
          do {
            activeFetchQueued = false;
            const wallet = useWalletStore();
            // 未登录不弹窗（已读态依赖 JWT）
            if (!wallet.isAuthenticated) {
              this.bannerVisible = false;
              continue;
            }
            const lang = resolveRequestLang();
            const res = await getActiveAnnouncement(lang);
            let item = res?.item ?? null;
            this.active = item;

            // /active 常缺 read：缺字段不能直接当未读弹，必须用列表 JWT 态校准
            let show = false;
            if (wallet.isAuthenticated && item?.id && item.read === true) {
              // 显式已读：绝不弹
              show = false;
            } else if (wallet.isAuthenticated) {
              try {
                // 多取几条，避免 active 不是列表第 1 条时校准落空
                const listRes = await getAnnouncements({
                  page: 1,
                  pageSize: 20,
                  lang,
                });
                // 与后端 USER_VISIBLE / unread-count 对齐：弹窗只认当前 PUBLISHED；
                // 勿把 ARCHIVED 历史未读提成横幅（下架当前横幅后会误弹旧公告）。
                const items = (Array.isArray(listRes?.items) ? listRes.items : []).filter(
                  (it) => isUserHistoryAnnouncement(it)
                );
                const match = item?.id
                  ? items.find((it) => it?.id && it.id === item!.id)
                  : null;

                if (match?.id) {
                  const merged = { ...item, ...match, read: match.read };
                  this.active = merged;
                  show = isAnnouncementUnread(merged);
                } else if (item?.id && item.read === false && isPublishedAnnouncement(item)) {
                  // 列表未命中但 active 仍是未读 PUBLISHED（短暂不一致）
                  show = true;
                } else {
                  // 无有效横幅时不回退到 ARCHIVED 历史未读
                  show = false;
                  if (!item?.id || !isPublishedAnnouncement(item)) {
                    this.active = null;
                  }
                }
              } catch (listErr) {
                logger.warn('[Announcements] list read calibration failed', listErr);
                // 校准失败时：仅当 active 明确 read===false 才弹，避免已读误弹
                show = !!(item?.id && item.read === false);
              }
            }
            this.bannerVisible = show;
          } while (activeFetchQueued);
        } catch (e) {
          logger.warn('[Announcements] active failed', e);
          // 503 等不硬编码为无公告；保留上次可见态
        } finally {
          this.activeLoading = false;
          activeFetchPromise = null;
          // finally 期间又有排队则补拉（如钱包恢复刚好落在收尾）
          if (activeFetchQueued) {
            activeFetchQueued = false;
            void this.fetchActive();
          }
        }
      })();
      return activeFetchPromise;
    },

    /** 登录后 / 开菜单 / 列表返回：未读角标 */
    async fetchUnreadCount() {
      const wallet = useWalletStore();
      if (!wallet.isAuthenticated) {
        this.unreadCount = 0;
        return;
      }
      if (this.unreadLoading) return;
      this.unreadLoading = true;
      try {
        const res = await getAnnouncementUnreadCount();
        const n = Number(res?.count);
        this.unreadCount = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
      } catch (e) {
        logger.warn('[Announcements] unread-count failed', e);
      } finally {
        this.unreadLoading = false;
      }
    },

    /** 公告列表分页；reset=true 从头拉 */
    async fetchList(opts?: { reset?: boolean }) {
      const wallet = useWalletStore();
      if (!wallet.isAuthenticated) {
        this.list = [];
        this.listTotal = 0;
        this.listHasMore = false;
        return;
      }
      if (this.listLoading) return;
      const reset = !!opts?.reset;
      if (reset) {
        this.listPage = 1;
        this.listHasMore = true;
      }
      if (!this.listHasMore && !reset) return;

      this.listLoading = true;
      const page = reset ? 1 : this.listPage;
      try {
        const res = await getAnnouncements({
          page,
          pageSize: this.listPageSize,
          lang: resolveRequestLang(),
        });
        // 防御：后端应已过滤 DELISTED/DRAFT；客户端再挡一层，避免旧网关或脏数据进历史
        const items = (Array.isArray(res?.items) ? res.items : []).filter((it) =>
          isUserHistoryAnnouncement(it)
        );
        this.list = reset ? items : [...this.list, ...items];
        this.listTotal = Number(res?.total) || 0;
        this.listPage = page + 1;
        this.listHasMore = this.list.length < this.listTotal;
      } catch (e) {
        logger.warn('[Announcements] list failed', e);
        if (reset) {
          this.list = [];
          this.listTotal = 0;
          this.listHasMore = false;
        }
      } finally {
        this.listLoading = false;
      }
    },

    /** 先本地隐藏再请求已读；关闭 / 我知道了走此路径 */
    async markRead(id: string, source: AnnouncementReadSource) {
      const aid = String(id || '').trim();
      if (!aid) return;

      if (this.active?.id === aid) {
        this.active = { ...this.active, read: true };
        this.bannerVisible = false;
      }

      this.list = this.list.map((it) =>
        it.id === aid ? { ...it, read: true } : it
      );
      // 角标只表示当前横幅；历史已读不减角标
      if (this.active?.id === aid && this.unreadCount > 0) {
        this.unreadCount = Math.max(0, this.unreadCount - 1);
      }

      const wallet = useWalletStore();
      if (!wallet.isAuthenticated) return;

      try {
        await markAnnouncementRead(aid, source);
      } catch (e) {
        logger.warn('[Announcements] mark-read failed', e);
      }
      void this.fetchUnreadCount();
    },

    /** 关闭 / 我知道了：标记已读并请求接口 */
    dismissBanner() {
      const id = String(this.active?.id || '');
      if (!id) {
        this.bannerVisible = false;
        return;
      }
      void this.markRead(id, 'BANNER_DISMISS');
    },

    openFromList(item: AnnouncementItem) {
      if (!item?.id) return;
      void this.markRead(item.id, 'LIST_OPEN');
    },

    clearSession() {
      this.active = null;
      this.bannerVisible = false;
      this.unreadCount = 0;
      this.list = [];
      this.listPage = 1;
      this.listTotal = 0;
      this.listHasMore = true;
    },
  },
});
